import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { Badge } from '@ctw/shared/components/badge';
import type { PatientRiskGapsTableColumn } from '@ctw/shared/content/patient-risk-gaps/components/resource-table';
import { confidenceLevelToRating } from '@ctw/shared/content/risk/risk-gap-confidence-level';
import { riskGapStatusBadgeDefinition } from '@ctw/shared/content/risk/risk-gap-status-badge-definition';
import { riskGapTypeBadgeDefinition } from '@ctw/shared/content/risk/risk-gap-type-badge-definition';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import { startCase } from 'lodash-es';

export const buildPatientRiskGapsColumnsV2 = (): Array<
  PatientRiskGapsTableColumn<RiskGapModel>
> => [
  {
    title: 'HCC',
    minWidth: '30%',
    render: (model) => (
      <div className={tw`flex flex-col font-semibold text-sm`}>
        {model.title} {`(HCC ${model.hccCode})`}
      </div>
    ),
    minimizedClassName: `${tw`order-3 w-full p-0 mb-2`}`,
    className: `${tw`order-3 md:order-1 p-0 md:p-4 mb-2 md:mb-0`}`,
  },
  {
    title: 'Type',
    render: (model) => {
      const diagnosisType = model.type === 'net-new' ? 'Newly Diagnosed' : startCase(model.type);
      return (
        <div className={tw`flex flex-col`}>
          <Badge size={'md'} {...riskGapTypeBadgeDefinition(model.type)} content={diagnosisType} />
        </div>
      );
    },
    minimizedClassName: `${tw`order-1 w-fit p-0 mb-2`}`,
    className: `${tw`order-1 md:order-2 w-fit p-0 md:p-4 mb-2 md:mb-0`}`,
  },
  {
    title: 'Status',
    render: (model) => (
      <div className={tw`flex flex-col`}>
        <Badge
          size={'md'}
          {...riskGapStatusBadgeDefinition(model)}
          icon={undefined}
          className={tw`text-content-subtle`}
        />
      </div>
    ),
    minimizedClassName: `${tw`order-2 w-fit p-0 mb-2`}`,
    className: `${tw`order-2 md:order-3 w-fit p-0 md:p-4 mb-2 md:mb-0`}`,
  },
  {
    title: 'Confidence',
    render: (model) => {
      const level =
        model.type === 'net-new' ? 'high' : confidenceLevelToRating(model.confidenceLevel);
      const display = level ? startCase(level) : '—';
      return (
        <div
          className={tw`hidden flex-col overflow-hidden text-ellipsis whitespace-nowrap text-sm md:flex `}
        >
          {display}
        </div>
      );
    },
    minimizedClassName: `${tw`hidden`}`,
  },
  {
    title: 'Last Updated',
    render: (model) => {
      return (
        <div className={tw`flex flex-col overflow-hidden text-ellipsis whitespace-nowrap text-sm`}>
          {formatFHIRDate(model.lastStatusUpdateDate)} by {model.lastUpdatedByDisplay}
        </div>
      );
    },
    minimizedClassName: `${tw`order-4`}`,
    className: `${tw`order-4`}`,
  },
];
